import { Button } from "@beweegbaas/ui";
import { ClientOnly, createFileRoute } from "@tanstack/react-router";
import { HistoryBackLink } from "#/components/back-link";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import type { MediakaartPdfPayload } from "#/modules/mediakaarten/pdf-components/pdf.mediakaart.card";
import { MediakaartPdfPreview } from "#/modules/mediakaarten/pdf-components/pdf.mediakaart.previewer.client";
import { useDownloadAllMediakaartenSociaalEmotioneelReeks } from "#/modules/mediakaarten/download-mediakaarten-reeks";
import { getSocialEmotionalDetail } from "#/modules/sociaal-emotioneel/social-emotional.functions";
import { sociaalEmotioneelThemaFields } from "#/modules/sociaal-emotioneel/types.sociaal-emotioneel";
import { isLeerjaarValid } from "#/modules/sociaal-emotioneel/validation.is-leerjaar-valid";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/social-emotional/mediakaart-preview/$reeksId",
)({
  loader: async ({ params }) => {
    const { sociaalEmotioneelThemasReeks: reeks } = await getSocialEmotionalDetail({
      data: { id: params.reeksId },
    });

    const cards: { leerjaarCombo: string; cardData: MediakaartPdfPayload }[] = [];
    for (const field of sociaalEmotioneelThemaFields) {
      const theme = reeks[field];
      if (theme && isLeerjaarValid(theme) === "valid") {
        cards.push({
          leerjaarCombo: theme.leerjaarCombo,
          cardData: theme,
        });
      }
    }

    return { reeks, cards };
  },
  component: SociaalEmotioneelMediakaartPreviewPage,
  errorComponent: SociaalEmotioneelMediakaartPreviewError,
});

function SociaalEmotioneelMediakaartPreviewError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

function SociaalEmotioneelMediakaartPreviewPage() {
  const { reeks, cards } = Route.useLoaderData();
  const { locale, schoolyear, school } = Route.useParams();
  const downloadMutation = useDownloadAllMediakaartenSociaalEmotioneelReeks();

  const handleDownload = () => {
    if (cards.length === 0) return;
    downloadMutation.mutate({ reeks });
  };

  return (
    <Page>
      <Page.Header>
        <HistoryBackLink
          fallback={{
            to: "/$locale/{-$schoolyear}/$school/social-emotional",
            params: { locale, schoolyear, school },
          }}
          className={"max-md:self-start max-sm:mb-4"}
        />
        <Page.Title>Mediakaart preview: {reeks.name}</Page.Title>

        <Page.Header.Actions>
          <Page.Header.MainAction>
            <Button
              className="shrink-0"
              size="xl"
              disabled={cards.length === 0}
              isPending={downloadMutation.isPending}
              onClick={handleDownload}
            >
              <IconMaterialSymbolsDownload className="size-4" />
              Download
            </Button>
          </Page.Header.MainAction>
        </Page.Header.Actions>
      </Page.Header>
      <Page.Container>
        <Page.Body>
          {cards.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              {cards.map((card) => (
                <div key={card.leerjaarCombo} className="flex flex-col gap-2">
                  <ClientOnly
                    fallback={
                      <div className="bg-muted aspect-297/210 w-full animate-pulse rounded" />
                    }
                  >
                    <MediakaartPdfPreview cardData={card.cardData} />
                  </ClientOnly>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">Deze reeks heeft geen mediakaarten om te tonen.</p>
          )}
        </Page.Body>
      </Page.Container>
    </Page>
  );
}
